import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  useDisclosure,
  Stack,
  Text,
  Link,
  useColorModeValue,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  VStack,
  Icon,
  Spinner,
  Badge,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import {
  HamburgerIcon,
  CloseIcon,
  BellIcon,
  MoonIcon,
  SunIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";
import { getSidebarModules } from "../services/sidebarService";
import {
  BsStickies,
  BsPeople,
  BsFileEarmarkText,
  BsPersonBadge,
  BsUiChecksGrid,
  BsGear,
  BsClipboardCheck,
  BsPlug,
  BsGraphUp,
  BsSignpost2,
  BsCalendarEvent,
  BsShieldCheck,
  BsCalendarCheck,
  BsClockHistory,
  BsCashStack,
  BsCreditCard,
  BsCreditCard2Back,
  BsBoxArrowRight,
  BsBank,
  BsReceipt,
  BsChatDots,
  BsMortarboard,
  BsPhone,
  BsBarChart,
  BsRocketTakeoff,
  BsCart,
  BsBoxSeam,
} from "react-icons/bs";

// Icon mapper - same as in Sidebar.js
const ICON_MAPPER = {
  BsPeople,
  BsFileEarmarkText,
  BsPersonBadge,
  BsUiChecksGrid,
  BsGear,
  BsClipboardCheck,
  BsPlug,
  BsGraphUp,
  BsSignpost2,
  BsCalendarEvent,
  BsShieldCheck,
  BsCalendarCheck,
  BsClockHistory,
  BsCashStack,
  BsCreditCard,
  BsCreditCard2Back,
  BsBoxArrowRight,
  BsBank,
  BsReceipt,
  BsChatDots,
  BsMortarboard,
  BsPhone,
  BsBarChart,
  BsRocketTakeoff,
  BsStickies,
  BsCart,
  BsBoxSeam,
};

const MotionLink = motion(Link);

const Links = [
  { label: "Dashboard", href: "/Dashboard", disabled: false },
  { label: "Core HR", href: "/core-hr/employee-database", disabled: false },
  { label: "Recruitment", href: "/recruiting/ats", disabled: false },
  { label: "Attendance", href: "/clockin", disabled: false },
  { label: "Payroll", href: "/payroll/run", disabled: false },
  { label: "Analytics", href: "/analytics/headcount", disabled: false },
  { label: "Settings", href: "/settings", disabled: false },
];

const NavLink = ({ label, href, disabled, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === href;

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onNavigate) onNavigate();
    navigate(href);
  };

  return (
    <MotionLink
      whileHover={!disabled ? { y: -1 } : {}}
      transition={{ duration: 0.15 }}
      px={3}
      py={1.5}
      rounded="md"
      fontWeight={isActive ? "semibold" : "medium"}
      fontSize="sm"
      position="relative"
      bg={isActive ? useColorModeValue("teal.50", "teal.900") : "transparent"}
      color={
        disabled
          ? useColorModeValue("gray.400", "gray.600")
          : isActive
            ? useColorModeValue("teal.700", "teal.200")
            : useColorModeValue("gray.700", "gray.200")
      }
      _hover={
        disabled
          ? {}
          : {
              textDecoration: "none",
              bg: isActive
                ? useColorModeValue("teal.50", "teal.900")
                : useColorModeValue("gray.100", "gray.700"),
            }
      }
      _after={
        isActive
          ? {
              content: '""',
              position: "absolute",
              bottom: "-2px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "80%",
              height: "2px",
              bg: useColorModeValue("teal.500", "teal.300"),
              borderRadius: "full",
            }
          : {}
      }
      pointerEvents={disabled ? "none" : "auto"}
      opacity={disabled ? 0.5 : 1}
      cursor={disabled ? "not-allowed" : "pointer"}
      onClick={handleClick}
    >
      {label}
    </MotionLink>
  );
};

const StickyNavbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSearchOpen,
    onOpen: onSearchOpen,
    onClose: onSearchClose,
  } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const { logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarModules, setSidebarModules] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchIndex, setSearchIndex] = useState(-1);

  useEffect(() => {
    onClose(); // Auto-close mobile nav when route changes
  }, [location.pathname]);

  // Fetch sidebar modules for search
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setSearchLoading(true);
        const data = await getSidebarModules();
        // Map icons in modules
        const modulesWithIcons = (data || []).map((module) => ({
          ...module,
          items: module.items.map((item) => ({
            ...item,
            icon: ICON_MAPPER[item.icon] || BsStickies,
          })),
        }));
        setSidebarModules(modulesWithIcons);
      } catch (err) {
        console.error("Error fetching modules for search:", err);
        setSidebarModules([]);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchModules();
  }, []);

  // Memoized search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const results = [];
    const query = searchQuery.toLowerCase();

    sidebarModules.forEach((section) => {
      section.items.forEach((item) => {
        if (
          item.label.toLowerCase().includes(query) ||
          item.to.toLowerCase().includes(query)
        ) {
          results.push({
            ...item,
            section: section.heading,
          });
        }
      });
    });

    return results;
  }, [searchQuery, sidebarModules]);

  const handleSearchSelect = (item) => {
    navigate(item.to);
    setSearchQuery("");
    onSearchClose();
    setSearchIndex(-1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchIndex((prev) =>
        prev < searchResults.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && searchIndex >= 0) {
      e.preventDefault();
      handleSearchSelect(searchResults[searchIndex]);
    } else if (e.key === "Escape") {
      onSearchClose();
    }
  };

  const handleLogout = () => {
    // clear auth state and redirect to login
    try {
      logout();
    } catch (err) {
      console.warn("Logout failed:", err);
    }
    navigate("/login");
  };

  const handleMyProfile = () => {
    navigate("/MyProfile");
  };

  const handleNotifications = () => {
    // TODO: Implement notifications panel
    console.log("Notifications clicked");
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <Box
      position="sticky"
      top="0"
      zIndex="999"
      bg={useColorModeValue("white", "gray.800")}
      px={6}
      backdropFilter="saturate(180%) blur(12px)"
      boxShadow="sm"
      borderBottom="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
    >
      <Flex h={14} alignItems="center" justifyContent="space-between" gap={8}>
        {/* Left Core: Logo + Nav Links */}
        <Flex align="center" gap={8} flex="1">
          {/* Brand Block - Clickable to go to Dashboard */}
          <Flex
            align="center"
            gap={2}
            flexShrink={0}
            onClick={handleLogoClick}
            cursor="pointer"
            transition="opacity 0.2s ease"
            _hover={{ opacity: 0.8 }}
            title="Go to Dashboard"
          >
            <Image src="/image.png" alt="NoraHuman" boxSize="40px" />
            <Text
              fontWeight="bold"
              fontSize="lg"
              bgGradient="linear(to-r, teal.400, blue.500)"
              bgClip="text"
              lineHeight="1"
            >
              NoraHuman
            </Text>
          </Flex>

          {/* Desktop Nav Links */}
          <HStack spacing={1} display={{ base: "none", md: "flex" }}>
            {Links.map((link) => (
              <NavLink key={link.label} {...link} />
            ))}
          </HStack>
        </Flex>

        {/* Right Utility Cluster */}
        <HStack spacing={2} flexShrink={0}>
          {/* Search */}
          <IconButton
            icon={<SearchIcon />}
            onClick={onSearchOpen}
            variant="ghost"
            size="sm"
            aria-label="Search menu"
            title="Search menu (Ctrl+K)"
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />

          {/* Dark/Light Toggle */}
          <IconButton
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
            size="sm"
            aria-label="Toggle theme"
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />

          {/* Notification Bell */}
          <IconButton
            icon={<BellIcon />}
            onClick={handleNotifications}
            variant="ghost"
            size="sm"
            aria-label="Notifications"
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />

          {/* Profile Menu */}
          <Menu>
            <MenuButton
              as={IconButton}
              variant="ghost"
              size="sm"
              icon={
                <Avatar size="sm" name="Abhishek Advani" src="/profile.png" />
              }
              _hover={{
                bg: useColorModeValue("gray.100", "gray.700"),
              }}
            />
            <MenuList>
              <MenuItem onClick={handleMyProfile}>My Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>

          {/* Hamburger */}
          <IconButton
            size="sm"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Toggle Navigation"
            display={{ md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            variant="ghost"
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />
        </HStack>
      </Flex>

      {/* Mobile Nav */}
      {isOpen && (
        <Box pb={3} display={{ md: "none" }}>
          <Stack as="nav" spacing={1}>
            {Links.map((link) => (
              <NavLink key={link.label} {...link} onNavigate={onClose} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Search Modal - Command Palette Style */}
      <Modal
        isOpen={isSearchOpen}
        onClose={onSearchClose}
        size="lg"
        motionPreset="fadeIn"
      >
        <ModalOverlay backdropFilter="blur(5px)" bg="rgba(0,0,0,0.4)" />
        <ModalContent
          borderRadius="lg"
          mt="60px"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          bg={useColorModeValue("white", "gray.800")}
          border="1px solid"
          borderColor={useColorModeValue("gray.200", "gray.700")}
          maxW="600px"
          overflow="hidden"
        >
          {/* Clean Search Header */}
          <Box
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor={useColorModeValue("gray.200", "gray.700")}
            bg={useColorModeValue("white", "gray.800")}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon
                  color={useColorModeValue("gray.400", "gray.500")}
                  fontSize="18px"
                />
              </InputLeftElement>
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchIndex(-1);
                }}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                size="md"
                border="none"
                _focus={{
                  outline: "none",
                  boxShadow: "none",
                }}
                fontSize="15px"
                pl={10}
              />
            </InputGroup>
          </Box>

          {/* Results */}
          <ModalBody p={0} maxH="400px" overflowY="auto">
            {searchLoading ? (
              <Flex justify="center" align="center" py={12}>
                <Spinner color="teal.500" size="md" />
              </Flex>
            ) : !searchQuery ? (
              <Box px={4} py={8} textAlign="center">
                <Text
                  fontSize="sm"
                  color={useColorModeValue("gray.500", "gray.400")}
                >
                  Start typing to search...
                </Text>
              </Box>
            ) : searchResults.length === 0 ? (
              <Box px={4} py={8} textAlign="center">
                <Text
                  fontSize="sm"
                  color={useColorModeValue("gray.500", "gray.400")}
                >
                  No results found
                </Text>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch">
                {searchResults.map((item, idx) => (
                  <Box
                    key={item.to}
                    px={4}
                    py={3}
                    cursor="pointer"
                    bg={
                      idx === searchIndex
                        ? useColorModeValue("teal.50", "teal.900")
                        : "transparent"
                    }
                    borderBottom="1px solid"
                    borderColor={useColorModeValue("gray.100", "gray.700")}
                    _last={{ borderBottom: "none" }}
                    _hover={{
                      bg: useColorModeValue("gray.50", "gray.700"),
                    }}
                    transition="background 0.15s ease"
                    onClick={() => handleSearchSelect(item)}
                    onMouseEnter={() => setSearchIndex(idx)}
                  >
                    <HStack spacing={3} align="center">
                      <Flex
                        w={8}
                        h={8}
                        borderRadius="md"
                        bg={useColorModeValue("teal.100", "teal.900")}
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <Icon
                          as={
                            item.icon instanceof Function
                              ? item.icon
                              : ICON_MAPPER[item.icon] || BsStickies
                          }
                          boxSize={4}
                          color={useColorModeValue("teal.600", "teal.300")}
                        />
                      </Flex>
                      <Box flex={1} minW={0}>
                        <HStack spacing={2} align="center">
                          <Text
                            fontWeight="500"
                            fontSize="14px"
                            color={useColorModeValue("gray.900", "gray.100")}
                          >
                            {item.label}
                          </Text>
                          <Text
                            fontSize="11px"
                            color={useColorModeValue("gray.500", "gray.400")}
                          >
                            {item.section}
                          </Text>
                        </HStack>
                        <Text
                          fontSize="12px"
                          color={useColorModeValue("gray.500", "gray.400")}
                          noOfLines={1}
                        >
                          {item.to}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>

          {/* Footer - keyboard hints */}
          {searchQuery && searchResults.length > 0 && (
            <Box
              px={4}
              py={2}
              borderTop="1px solid"
              borderColor={useColorModeValue("gray.200", "gray.700")}
              bg={useColorModeValue("gray.50", "gray.750")}
            >
              <HStack justify="space-between" fontSize="11px" color="gray.500">
                <Text>↑↓ navigate</Text>
                <Text>⏎ select</Text>
                <Text>esc close</Text>
              </HStack>
            </Box>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StickyNavbar;
